/**
 * Templates AI Edit Feature Types
 *
 * Type definitions specific to the template builder feature.
 * Extends the shared AG-UI types from ai-chat-core.
 */

import type { AgUiChatState } from '@features/ai-chat-core';
import type {
  DraftSectionResponse,
  DraftTemplateResponse,
  TemplateBuildConfirmRequest,
  TemplateBuildStatusResponse,
  TemplateBuildUploadResponse,
} from '@store/generated/templates';

// ============================================================================
// Type Declaration Merging - Extend RootState
// ============================================================================

declare module '@store/types' {
  interface DynamicFeatureState {
    'templatesAiEdit/ui'?: TemplateBuildState;
    'templatesAiEdit/streaming'?: unknown; // RTK Query streaming cache
  }
}

// ============================================================================
// Re-export Generated Types with Aliases
// ============================================================================

export type { DraftSectionResponse, DraftTemplateResponse, TemplateBuildConfirmRequest, TemplateBuildStatusResponse, TemplateBuildUploadResponse };

// ============================================================================
// Draft Template Types
// ============================================================================

/**
 * Draft section from the build workflow
 * Mirrors DraftSectionResponse but with camelCase for frontend use
 */
export interface DraftSection {
  id: string;
  title: string;
  order: number;
  content: string;
  exampleOutput: string | null;
}

/**
 * Draft template being built through conversation
 */
export interface DraftTemplate {
  title: string;
  description: string | null;
  sections: DraftSection[];
}

/**
 * Convert API response to frontend DraftTemplate
 */
export function toDraftTemplate(response: DraftTemplateResponse): DraftTemplate {
  return {
    title: response.title,
    description: response.description ?? null,
    sections: response.sections.map(s => ({
      id: s.id,
      title: s.title,
      order: s.order,
      content: s.content,
      exampleOutput: s.example_output ?? null,
    })),
  };
}

/**
 * Convert API section response to frontend DraftSection
 */
export function toDraftSection(response: DraftSectionResponse): DraftSection {
  return {
    id: response.id,
    title: response.title,
    order: response.order,
    content: response.content,
    exampleOutput: response.example_output ?? null,
  };
}

// ============================================================================
// Build State Types
// ============================================================================

/**
 * Build status values
 */
export type BuildStatus =
  | 'idle'
  | 'uploading'
  | 'processing'
  | 'structure_ready'
  | 'ready_for_review'
  | 'completed'
  | 'failed';

/**
 * Template build state - extends base AG-UI chat state
 */
export interface TemplateBuildState extends AgUiChatState {
  // Build session
  taskId: string | null;
  buildStatus: BuildStatus;
  progressMessage: string | null;

  // Document metadata (for PDFs, available early in processing)
  documentTitle: string | null;
  documentSummary: string | null;

  // Draft template state (from polling or ACTIVITY_SNAPSHOT)
  draftTemplate: DraftTemplate | null;

  // Section selection for context
  selectedSectionId: string | null;

  // After save completes
  createdTemplateId: string | null;

  // Flag to trigger status refetch after chat completes
  needsStatusRefetch: boolean;
}

// ============================================================================
// Request Payload Types
// ============================================================================

/**
 * Template build chat message request
 */
export interface TemplateBuildChatRequest {
  taskId: string;
  threadId: string | null;
  runId: string;
  messages?: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
  }>;
  resume?: {
    interruptId: string;
    payload:
      | { confirmed: boolean }
      | { selected_ids: string[] }
      | { text: string }
      | Record<string, { selected_ids?: string[]; confirmed?: boolean }>;
  };
  selected_section_id?: string;
}

/**
 * Union type for all request payloads
 */
export type TemplateBuildRequestPayload = TemplateBuildChatRequest;

// ============================================================================
// Streaming Data Types
// ============================================================================

/**
 * Streaming cache data for template build chat
 */
export interface TemplateBuildStreamData {
  isActive: boolean;
  isDone: boolean;
}

// ============================================================================
// Upload Types
// ============================================================================

/**
 * Upload mode - PDF file or pasted text
 */
export type UploadMode = 'pdf' | 'text';

/**
 * Upload state
 */
export interface UploadState {
  mode: UploadMode;
  file: File | null;
  text: string;
  isUploading: boolean;
  error: string | null;
}

